/**
 * SendFax — compose a STANDALONE fax (not tied to a patient release): upload a
 * PDF, pick a destination (free-entered or a saved provider office), optionally
 * prepend a WCR cover sheet with a note, and send. Backed by
 * POST /api/crm/fax/send (multipart). The "Provider offices" sub-tab manages the
 * directory this picker reads from.
 */
import { useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Send, Upload, X } from 'lucide-react';
import api from '../../../services/api';
import { ProviderOffice } from '../../types';
import { Alert } from '../ui';

const faxValid = (fax: string) => {
  const d = (fax || '').replace(/\D/g, '');
  return d.length === 10 || (d.length === 11 && d.startsWith('1'));
};
const fmtFax = (fax: string | null) => {
  const d = (fax || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : fax || '';
};

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const lblCls = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

const MAX_BYTES = 50 * 1024 * 1024;

export default function SendFax({ onSent }: { onSent?: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [toNumber, setToNumber] = useState('');
  const [toName, setToName] = useState('');
  const [note, setNote] = useState('');
  const [cover, setCover] = useState(true);
  const [officeId, setOfficeId] = useState('');
  const [directory, setDirectory] = useState<ProviderOffice[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    api.get('/api/crm/provider-offices')
      .then((res) => { if (alive) setDirectory((res.data?.offices || []).filter((o: ProviderOffice) => o.fax_number)); })
      .catch(() => { /* directory is optional — free entry still works */ });
    return () => { alive = false; };
  }, []);

  const pickOffice = (id: string) => {
    setOfficeId(id);
    const o = directory.find((d) => d.id === id);
    if (o) {
      if (o.fax_number) setToNumber(fmtFax(o.fax_number));
      if (o.office_name) setToName(o.office_name);
    }
  };

  const onFile = (f: File | null) => {
    setError(null);
    if (!f) { setFile(null); return; }
    if (f.type !== 'application/pdf') { setError('Only PDF files can be faxed.'); return; }
    if (f.size > MAX_BYTES) { setError('That PDF is larger than 50MB.'); return; }
    setFile(f);
  };

  const canSend = Boolean(file) && faxValid(toNumber) && !sending;

  const reset = () => {
    setFile(null); setToNumber(''); setToName(''); setNote(''); setCover(true); setOfficeId('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const send = async () => {
    if (!canSend || !file) return;
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('to_number', toNumber.trim());
      if (toName.trim()) form.append('to_name', toName.trim());
      if (note.trim()) form.append('note', note.trim());
      form.append('cover', cover ? 'true' : 'false');
      if (officeId) form.append('office_id', officeId);
      await api.post('/api/crm/fax/send', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNotice('Fax queued.');
      reset();
      onSent?.();
    } catch (e: any) {
      const d = e?.response?.data || {};
      const base = d.detail || d.error || e.message || 'Could not send the fax.';
      setError(d.hint ? `${base} — ${d.hint}` : base);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-xl space-y-4">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Send a one-off fax to any number. Upload a PDF, choose a destination, and we'll fax it from the West Clinical fax line.
      </p>

      {notice && (
        <Alert tone="info" size="sm" icon={false}>{notice}</Alert>
      )}
      {error && (
        <Alert tone="error" size="sm" icon={false}>{error}</Alert>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="space-y-4">
          {/* File */}
          <div>
            <label className={lblCls}>PDF to fax</label>
            {file ? (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
                <span className="inline-flex min-w-0 items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <FileText className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
                  <span className="truncate">{file.name}</span>
                  <span className="shrink-0 text-xs text-gray-400">{(file.size / 1024).toFixed(0)} KB</span>
                </span>
                <button type="button" onClick={() => onFile(null)} aria-label="Remove file" className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-3 py-6 text-sm font-medium text-gray-500 hover:border-primary-400 hover:text-primary-600 dark:border-gray-600 dark:text-gray-400 dark:hover:border-primary-500"
              >
                <Upload className="h-4 w-4" /> Choose a PDF
              </button>
            )}
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0] || null)} />
          </div>

          {/* Destination */}
          {directory.length > 0 && (
            <div>
              <label className={lblCls}>Use a saved office <span className="font-normal">(optional)</span></label>
              <select className={inputCls} value={officeId} onChange={(e) => pickOffice(e.target.value)}>
                <option value="">Pick from directory…</option>
                {directory.map((d) => (
                  <option key={d.id} value={d.id}>{d.office_name}{d.location ? ` — ${d.location}` : ''} ({fmtFax(d.fax_number)})</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={lblCls}>Fax number</label>
              <input
                type="tel"
                inputMode="tel"
                className={inputCls}
                value={toNumber}
                onChange={(e) => { setToNumber(e.target.value); setOfficeId(''); }}
                placeholder="(919) 555-0142"
              />
              {toNumber.trim() !== '' && !faxValid(toNumber) && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Enter a 10-digit US fax number.</p>
              )}
            </div>
            <div>
              <label className={lblCls}>Recipient name <span className="font-normal">(optional)</span></label>
              <input className={inputCls} value={toName} onChange={(e) => setToName(e.target.value)} placeholder="Wake Internal Medicine" />
            </div>
          </div>

          {/* Cover sheet */}
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={cover} onChange={(e) => setCover(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            <span className="text-sm text-gray-700 dark:text-gray-200">
              Add a West Clinical cover sheet
              <span className="block text-xs text-gray-400">Includes the recipient, our return fax number, and your note below.</span>
            </span>
          </label>
          {cover && (
            <div>
              <label className={lblCls}>Note for the cover sheet <span className="font-normal">(optional)</span></label>
              <textarea className={`${inputCls} min-h-[80px]`} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="A short message to print on the cover page…" />
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={send}
            disabled={!canSend}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Sending…' : 'Send fax'}
          </button>
        </div>
      </div>
    </div>
  );
}
